import { isObject } from 'lodash-es'
import { z } from 'zod'

import { CustomerCargoDetailsResponse } from '@forto-dev/shipment-models/dist/houseShipmentModels/customer/customerCargoDetails.response'
import { CustomerFilterLookupDataResponse } from '@forto-dev/shipment-models/dist/houseShipmentModels/customer/customerFilterLookupData.response'
import { Pagination } from '@forto-dev/shipment-types'
import { TransportScheme } from '@freighthub/freighthub-transportplanning-sdk'

import Client from '@/api/client'
import { ApiConfig, clientFactory, EndpointConfig } from '@/api/clientFactory'
import { getConfig } from '@/app/config'
import { canAuthorizeBooking, getBooking } from '@/features/bookings'

import { getBookings } from './bookings.service'
import { bookingRequiredActionsMapper, shipmentRequiredActionsMapper } from './requiredActionsMapper'
import { shipmentV2ToOverviewItem } from './shipmentToOverviewItem'
import { CostType, Currency, PriceBreakdownResponse, PriceBreakdownSchema, PriceBreakdownType } from '../domain/costs'
import { CustomsStatus } from '../domain/customs'
import { DemurrageDetentionCharges } from '../domain/demurrage-detention'
import { DetailedShipment, DetailedShipmentRequiredAction } from '../domain/detailed-shipment'
import { Emissions } from '../domain/emissions'
import { OverviewItem, OverviewItemResponse } from '../domain/overviewItem'
import { RequiredAction, RequiredActionSearchLocation } from '../domain/requiredAction'
import { RequiredActionType } from '../domain/requiredActionTypes'
import { SearchOptions } from '../domain/searchOptions'
import { Shipment } from '../domain/shipment'
import { ShipmentExport } from '../domain/shipmentExport'
import { ShipmentsV1SearchResult } from '../domain/shipments'
import { BookingPageParams, CustomerHouseShipmentResponse } from '../types'

const client = new Client(getConfig().API_ENDPOINT ?? '')
const revenueBasePath = '/api/revenue'

export type ShipmentPageParams = {
  from: number
  size: number
  sortField: keyof Shipment
  sortDirection: string
  query?: string
  filter?: string
  exactFilters?: string
  rangeFilter?: unknown
}

export type RequiredActionPageParams = {
  page: number
  size: number
  sortField?: string
  sortDirection?: string
  shipmentId?: string
  bookingId?: string
}

export type ShipmentV2Response = {
  limit: number
  offset: number
  orderBy: string
  results: CustomerHouseShipmentResponse[]
  total: number
}

export const getShipmentsV2 = async ({ pageParam }: { pageParam: Pagination }): Promise<OverviewItemResponse> => {
  const { results, total } = await client.get<ShipmentV2Response>('/api/shipments/customer/house-shipments/v2', {
    params: {
      ...pageParam,
    },
  })
  const items: OverviewItem[] = []
  results.forEach((shipment) => {
    const overviewItem = shipmentV2ToOverviewItem(shipment)
    if (overviewItem) {
      items.push(overviewItem)
    }
  })
  return {
    items,
    total,
  }
}

export const getShipmentsLookupV2 = async (): Promise<CustomerFilterLookupDataResponse> => {
  const results = await client.get<CustomerFilterLookupDataResponse>(
    '/api/shipments/customer/house-shipments/v2/lookup'
  )
  return results
}

export const getShipmentCargoSummary = async (code: string): Promise<CustomerCargoDetailsResponse[]> => {
  const results = await client.get<CustomerCargoDetailsResponse[]>(
    `/api/shipments/customer/house-shipments/v2/${code}/cargo`
  )
  return results
}

export const getShipments = async ({
  pageParam,
}: {
  pageParam: ShipmentPageParams
}): Promise<ShipmentsV1SearchResult> => {
  const result = await client.get<ShipmentsV1SearchResult>('/api/shipping/search/literalTerm/shipments', {
    params: pageParam,
  })

  return result
}

export const IGNORE_ACTIONS_LIST: RequiredActionType[] = [
  'provideSupplierInformation',
  'provideConsigneeInformation',
  'providePickupDetails',
  'provideDeliveryDetails',
  'provideIncoTerms',
  'provideBLType',
]

export const getRequiredActions = async ({
  pageParam,
}: {
  pageParam: RequiredActionPageParams
}): Promise<{ results: RequiredAction[]; total: number }> => {
  const { page = 1, size = 10 } = pageParam
  let totalItems = 0
  let shipmentIdsToFetch = []
  const additionalShipmentData: Record<
    string,
    {
      origin: RequiredActionSearchLocation
      destination: RequiredActionSearchLocation
    }
  > = {}
  let requiredActionsByShipmentID: { [shipmentID: string]: DetailedShipmentRequiredAction[] } = {}
  let bookingRequiredActions: RequiredAction[] = []

  const shipmentsPageParams: ShipmentPageParams = {
    from: (page - 1) * size,
    size: size,
    sortField: (pageParam.sortField as keyof Shipment) || '_id_num',
    sortDirection: pageParam.sortDirection || 'desc',
    rangeFilter: '{"required_actions_count":{"gte":1}}',
  }

  const bookingsPageParams: BookingPageParams = {
    page,
    pageSize: size,
    sort: 'bookingDate|desc',
    state: 'open',
    shipmentID: '',
  }

  if (!pageParam.shipmentId && !pageParam.bookingId) {
    const { hits, total } = await getShipments({ pageParam: shipmentsPageParams })
    const { items, totalItems: totalBookingItems } = await getBookings({ pageParam: bookingsPageParams })
    if (hits.length === 0 && items.length === 0) {
      return { results: [], total: 0 }
    }
    shipmentIdsToFetch = hits.map((shipment) => shipment.data.id)
    hits.forEach((shipment) => {
      additionalShipmentData[shipment.data.id] = {
        origin: shipment.data._origin,
        destination: shipment.data._destination,
      }
    })
    totalItems = total + totalBookingItems
    requiredActionsByShipmentID = await getRequiredActionsForShipmentIDs(shipmentIdsToFetch)
    bookingRequiredActions = items
      .filter((booking) => canAuthorizeBooking(booking))
      .map((booking) => bookingRequiredActionsMapper(booking))
  } else {
    if (pageParam.shipmentId) {
      shipmentIdsToFetch.push(pageParam.shipmentId)
      const shipment = await getShipment(pageParam.shipmentId)
      additionalShipmentData[pageParam.shipmentId] = {
        origin: { address: shipment.origin, port: shipment.origin },
        destination: { address: shipment.destination, port: shipment.destination },
      }
      totalItems = 1
      requiredActionsByShipmentID = { [pageParam.shipmentId]: shipment.requiredActions }
    } else if (pageParam.bookingId) {
      const booking = await getBooking(pageParam.bookingId)
      const canAuthorize = canAuthorizeBooking(booking)
      if (!canAuthorize) {
        return { results: [], total: 0 }
      }

      return { results: [bookingRequiredActionsMapper(booking)], total: 1 }
    }
  }

  return {
    results: [
      ...Object.entries(requiredActionsByShipmentID)
        .flatMap(([shipmentID, requiredActions]) =>
          requiredActions.map((action) => ({
            ...action,
            origin: additionalShipmentData[shipmentID]?.origin,
            destination: additionalShipmentData[shipmentID]?.destination,
          }))
        )
        .filter((action) => !action.completed && !IGNORE_ACTIONS_LIST.includes(action.type))
        .map((reqAction) => shipmentRequiredActionsMapper(reqAction))
        .sort((a, b) => {
          // Sort by due date
          if (a.customerDueDate && b.customerDueDate) {
            return a.customerDueDate.getTime() - b.customerDueDate.getTime()
          }
          // if a due date is not present, send it to the end
          if (a.customerDueDate) {
            return -1
          }
          if (b.customerDueDate) {
            return 1
          }
          return 0
        }),
      ...bookingRequiredActions,
    ],
    total: totalItems,
  }
}

const convertSearchOptionsToQueryParam = (options: SearchOptions) => {
  return {
    size: options.size == null ? 50 : options.size,
    from: options.from || 0,
    sortField: options.sortBy || '',
    sortDirection: options.sortDirection || '',
    filter: JSON.stringify(options.filter || {}),
    exactFilters: JSON.stringify(options.exactFilters || {}),
    rangeFilter: JSON.stringify(options.rangeFilter || {}),
    highlight: options.highlight || '',
    v2flow: false, // this is hardcoded for old SHIP to prevent the UI from breaking
    ...(options.query && { query: options.query }),
  }
}

export const getAggregatedShipments = async ({
  searchQuery,
}: {
  searchQuery: SearchOptions
}): Promise<ShipmentExport> => {
  const params = convertSearchOptionsToQueryParam(searchQuery)
  return await client.get<ShipmentExport>('/api/shipping/aggregations/containers', {
    params,
  })
}
const getURLSearchStringWithCommaSeparatedArrays = (params: object) => {
  const u = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      u.append(key, value.join(','))
    } else {
      u.append(key, value)
    }
  })
  return u.toString()
}

export const getRequiredActionsForShipmentIDs = async (
  shipmentIDs: string[]
): Promise<{ [shipmentID: string]: DetailedShipmentRequiredAction[] }> => {
  return await client.get(`/api/shipping/api/required-actions/shipments`, {
    params: {
      shipmentIds: shipmentIDs,
    },
    serializationStrategy: getURLSearchStringWithCommaSeparatedArrays,
  })
}
export const getShipment = async (shipmentId: string): Promise<DetailedShipment> => {
  return await client.get<DetailedShipment>(`/api/shipping/api/shipments/${shipmentId}`)
}

export const getShipmentEmissions = async (shipmentId: string): Promise<Emissions> => {
  return await client.get<Emissions>(`/api/carbon-emissions/emissions?shipmentId=${shipmentId}`)
}

export const getShipmentDemurrageDetention = async (shipmentId: string): Promise<DemurrageDetentionCharges> => {
  return await client.get<DemurrageDetentionCharges>(`/api/dem-det/rates/selling/${shipmentId}`)
}

export const getShipmentTransportScheme = async (shipmentId: string): Promise<TransportScheme> => {
  return await client.get<TransportScheme>(`/api/shipping/api/shipments/${shipmentId}/transportScheme`)
}

export const getShipmentCustomsStatus = async (shipmentId: string): Promise<CustomsStatus> => {
  return await client.get<CustomsStatus>(`/api/customs-status/customs-status/${shipmentId}`)
}

export type UpdateShipmentInformationProps = {
  shipmentId: string
  data: Partial<DetailedShipment>
}

export const updateShipmentInformation = async (props: UpdateShipmentInformationProps): Promise<void> => {
  await client.put(`/api/shipping/api/shipments/${props.shipmentId}`, props.data)
}

export type UpdateShipmentShipperInformationProps = {
  customerId: string
  shipmentId: string
  data: {
    contactId?: string
    companyName?: string
    contactPerson?: string
    address?: string
    phone?: string
    email?: string
  }
}

export const updateShipmentShipperInformation = async (props: UpdateShipmentShipperInformationProps): Promise<void> => {
  const body = {
    shipmentId: props.shipmentId,
    contact: {
      _id: props.data.contactId,
      address: props.data.address,
      companyName: props.data.companyName,
      contactPerson: props.data.contactPerson,
      email: props.data.email,
      phone: props.data.phone,
    },
  }

  await client.post(
    `/api/shipping/api/customers/${props.customerId}/shipperContact/${props.data.contactId ?? ''}`,
    body
  )
}

export type UpdateShipmentConsigneeInformationProps = {
  customerId: string
  shipmentId: string
  data: {
    contactId?: string
    companyName?: string
    contactPerson?: string
    address?: string
    phone?: string
    email?: string
  }
}

export const updateShipmentConsigneeInformation = async (
  props: UpdateShipmentConsigneeInformationProps
): Promise<void> => {
  const body = {
    shipmentId: props.shipmentId,
    contact: {
      _id: props.data.contactId,
      address: props.data.address,
      companyName: props.data.companyName,
      contactPerson: props.data.contactPerson,
      email: props.data.email,
      phone: props.data.phone,
    },
  }

  await client.post(
    `/api/shipping/api/customers/${props.customerId}/consigneeContact/${props.data.contactId ?? ''}`,
    body
  )
}

type CurrencyMap = Record<string, number>

const sortCurrencies = (currenciesMap: CurrencyMap) => {
  return Object.entries(currenciesMap)
    .map(([currency, value]) => ({ currency, value }))
    .sort((a, b) => b.value - a.value)
}

const calculateTotalCosts = (costs: PriceBreakdownResponse) => {
  const totalCosts = {} as CurrencyMap

  const totalCostMap = costs.categories.reduce(
    (categoryAcc, category) => {
      const { lineItems, type } = category
      if (!lineItems.length) {
        return categoryAcc
      }

      const categoryTotalCost = lineItems.reduce((acc, { originalValue }) => {
        acc[originalValue.currency] = (acc[originalValue.currency] ?? 0) + originalValue.value
        totalCosts[originalValue.currency] = (totalCosts[originalValue.currency] ?? 0) + originalValue.value
        return acc
      }, {} as CurrencyMap)

      categoryAcc[type] = sortCurrencies(categoryTotalCost)
      return categoryAcc
    },
    {} as Record<CostType, Currency[]>
  )

  return {
    categoryCosts: totalCostMap,
    totalCosts: sortCurrencies(totalCosts),
  }
}

export const shipmentsConfig: ApiConfig<{
  getPriceBreakdowns: EndpointConfig<string, PriceBreakdownResponse, PriceBreakdownType>
}> = {
  getPriceBreakdowns: {
    requestConfig: (shipmentId: string) => ({
      endpoint: `${revenueBasePath}/price-breakdowns/${shipmentId}`,
      method: 'get',
      deserializationStrategy: z.preprocess((data) => {
        if (isObject(data)) {
          const costsWithTotalCosts = { ...data, ...calculateTotalCosts(data as PriceBreakdownResponse) }
          return costsWithTotalCosts
        }
        return {}
      }, PriceBreakdownSchema),
    }),
  },
}
export const shipmentsClient = clientFactory(shipmentsConfig)
